import { Locale as LocaleSchema, type Locale } from '@morales/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { dictionaries, type AppMessageKey } from './messages';

export const LOCALE_STORAGE_KEY = 'morales.locale';
export const DEFAULT_LOCALE: Locale = 'en';
/** es-US keeps US money/number formats ($1,234.50) with Spanish words. */
const INTL_TAG: Record<Locale, string> = { es: 'es-US', en: 'en-US' };

export function readStoredLocale(): Locale {
  try {
    const parsed = LocaleSchema.safeParse(window.localStorage.getItem(LOCALE_STORAGE_KEY));
    return parsed.success ? parsed.data : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE; // storage blocked (private mode, locked-down browser)
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // storage blocked: the choice lives in memory for this session
  }
}

export type Translate = (key: AppMessageKey) => string;

interface LocaleContextValue {
  locale: Locale;
  /** BCP 47 tag for Intl date and money formatting. */
  intlLocale: string;
  t: Translate;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** persist=false: a choice for this page only (a customer's document opened by staff keeps their own language). */
export function LocaleProvider({ children, initialLocale, persist = true }: { children: ReactNode; initialLocale?: Locale; persist?: boolean }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? readStoredLocale());

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      if (persist) writeStoredLocale(next);
    },
    [persist],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => {
    const dict = dictionaries[locale];
    return {
      locale,
      intlLocale: INTL_TAG[locale],
      t: (key) => dict[key],
      setLocale,
      toggleLocale: () => setLocale(locale === 'es' ? 'en' : 'es'),
    };
  }, [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}

export function useT(): Translate {
  return useLocale().t;
}

/** ES | EN switch for the header. Language codes are shown as-is in both languages. */
export function LocaleToggle() {
  const { locale, setLocale, t } = useLocale();
  return (
    <div className="lang-toggle" role="group" aria-label={t('language')}>
      {(['es', 'en'] as const).map((l) => (
        <button key={l} type="button" lang={l} aria-pressed={locale === l} onClick={() => setLocale(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
